import { useId, useMemo, useState } from 'react';

import { ColumnDef, PaginationState, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { ActivityMatter } from '@/../../backend/src/usage/dto/activity-overview.response.dto';
import { useGetActivityMatters, useGetActivityOverview } from '@/api/queries/useGetActivityOverview';
import { Icon } from '@/new/components/icon';
import { SettingsSectionHeading } from '@/new/components/settings-section';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { Switch } from '@/new/components/shadcn/switch';
import { ServerSideScrollableTable } from '@/new/components/table/server-side-scrollable-table';
import { TooltipIcon } from '@/new/components/tooltip-icon';

const PAGE_SIZE = 15;
const number = new Intl.NumberFormat('en-GB');
const compact = new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 });
const month = new Intl.DateTimeFormat('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });

type ChartDatum = { month: string; label: string; value: number };
type TooltipRow = { label: string; value: number };

function BreakdownTooltip({ rows }: { rows: TooltipRow[] }) {
  return (
    <div className="w-44 space-y-1.5">
      {rows.map((row) => (
        <div key={row.label} className="flex justify-between gap-6">
          <span>{row.label}</span>
          <span className="font-medium tabular-nums">{number.format(row.value)}</span>
        </div>
      ))}
    </div>
  );
}

function SummaryMetricCard({
  title,
  value,
  data,
  tooltip,
}: {
  title: React.ReactNode;
  value: number;
  data: ChartDatum[];
  tooltip: React.ReactNode;
}) {
  const gradientId = `summary-metric-${useId().replaceAll(':', '')}`;
  const chartData = [
    { month: 'start-2', label: '', value: 0 },
    { month: 'start-1', label: '', value: 0 },
    ...data,
    { month: 'end-1', label: '', value: 0 },
    { month: 'end-2', label: '', value: 0 },
  ];

  return (
    <div className="relative flex min-w-0 items-end justify-between gap-4 overflow-hidden rounded-xl border border-gray-200/70 bg-white px-5 py-4">
      <div className="absolute inset-x-0 bottom-[12px] h-0.5 bg-teal-400" aria-hidden="true" />
      <div className="relative z-10 min-w-0">
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          {title}
          <TooltipIcon side="right" tooltipContent={tooltip} contentClassName="max-w-72" />
        </div>
        <p className="mt-0.5 pb-2 text-2xl font-semibold tabular-nums">{number.format(value)}</p>
      </div>
      <div className="relative h-13 w-38 shrink-0 translate-y-[7px] bg-white" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 4, right: 0, bottom: 4, left: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-teal-400)" stopOpacity={0.25} />
                <stop offset="100%" stopColor="var(--color-teal-400)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="value"
              stroke="var(--color-teal-400)"
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function MetricChartCard<T extends ChartDatum>({
  title,
  value,
  data,
  getTooltipRows = (item) => [{ label: title, value: item.value }],
}: {
  title: string;
  value: number;
  data: T[];
  getTooltipRows?: (item: T) => TooltipRow[];
}) {
  const [isCumulative, setIsCumulative] = useState(true);
  const chartData = useMemo(() => {
    if (!isCumulative) return data;

    const totals: Record<string, number> = {};
    return data.map(
      (item) =>
        Object.fromEntries(
          Object.entries(item).map(([key, itemValue]) => {
            if (typeof itemValue !== 'number') return [key, itemValue];

            totals[key] = (totals[key] ?? 0) + itemValue;
            return [key, totals[key]];
          }),
        ) as T,
    );
  }, [data, isCumulative]);
  const gradientId = `activity-${title.toLowerCase().replaceAll(' ', '-')}`;
  const switchId = `${gradientId}-cumulative`;
  return (
    <section className="rounded-xl border border-gray-200/70 bg-white">
      <div className="px-5.5 pt-4.5 pr-3">
        <div className="flex items-center justify-between gap-4">
          <p className="text-[13px] text-gray-500">{title}</p>
          <div className="flex items-center gap-0.75">
            <label htmlFor={switchId} className="cursor-pointer text-xs text-gray-600">
              Cumulative
            </label>
            <Switch
              className="scale-70 data-[state=checked]:bg-gray-700"
              id={switchId}
              checked={isCumulative}
              onCheckedChange={setIsCumulative}
            />
          </div>
        </div>
        <p className="mt-1 text-3xl font-semibold tracking-tight text-gray-900 tabular-nums">{number.format(value)}</p>
      </div>

      <div className="px-3 pt-4.5 pr-5 pb-1">
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-teal-300)" stopOpacity={0.2} />
                <stop offset="80%" stopColor="var(--color-teal-300)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-gray-200)" />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={32}
              tickMargin={12}
              tick={{ fill: 'var(--color-gray-500)', fontSize: 12 }}
              height={48}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              width={48}
              allowDecimals={false}
              tickFormatter={(value: number) => compact.format(value)}
              tick={{ fill: 'var(--color-gray-500)', fontSize: 12 }}
            />
            <Tooltip
              cursor={{ stroke: 'var(--color-gray-200)' }}
              wrapperStyle={{ zIndex: 20 }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const item = payload[0].payload as T;
                return (
                  <div className="min-w-44 rounded-lg border bg-white p-3 text-sm shadow-lg/4">
                    <p className="mb-2 border-b pb-2 font-medium text-gray-800">{item.label}</p>
                    <div className="space-y-1.5">
                      {getTooltipRows(item).map((row) => (
                        <div key={row.label} className="flex justify-between gap-6 text-gray-600">
                          <span>{row.label}</span>
                          <span className="font-medium text-gray-900">{number.format(row.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="var(--color-teal-400)"
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function ChronologiesChartCard({
  factTotal,
  documentTotal,
  data,
}: {
  factTotal: number;
  documentTotal: number;
  data: Array<ChartDatum & { factChronologies: number; documentChronologies: number }>;
}) {
  return (
    <MetricChartCard
      title="Chronologies"
      value={factTotal + documentTotal}
      data={data}
      getTooltipRows={(item) => [
        { label: 'Fact chronologies', value: item.factChronologies },
        { label: 'Document chronologies', value: item.documentChronologies },
      ]}
    />
  );
}

const matterColumns: ColumnDef<ActivityMatter>[] = [
  {
    accessorKey: 'matterName',
    header: 'Matter',
    enableSorting: false,
    cell: ({ getValue }) => (
      <span className="block max-w-72 truncate font-medium text-gray-800">{getValue<string>()}</span>
    ),
  },
  ...(
    [
      ['documents', 'Documents'],
      ['chronologies', 'Chronologies'],
      ['inconsistencies', 'Inconsistencies'],
      ['entities', 'Entities'],
    ] as const
  ).map<ColumnDef<ActivityMatter>>(([id, label]) => ({
    id,
    header: () => <span className="ml-auto">{label}</span>,
    accessorFn: (row) => (id === 'chronologies' ? row.chronologies + row.docChronologies : row[id]),
    enableSorting: false,
    cell: ({ getValue }) => <span className="block text-right tabular-nums">{number.format(getValue<number>())}</span>,
  })),
];

export function ActivityDashboard({ organisationId }: { organisationId: string }) {
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });
  const { data, isLoading } = useGetActivityOverview(organisationId);
  const { data: matterData, isLoading: areMattersLoading } = useGetActivityMatters(
    organisationId,
    pagination.pageIndex + 1,
    pagination.pageSize,
  );
  const matterTable = useReactTable({
    data: matterData?.rows ?? [],
    columns: matterColumns,
    state: { pagination },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: matterData?.pageCount ?? 0,
    onPaginationChange: setPagination,
    getRowId: (row) => row.matterId,
  });

  if (isLoading) {
    return (
      <div className="flex h-full animate-pulse items-center justify-center gap-2 text-sm text-gray-500">
        <Icon className="lucide--loader animate-spin ease-in-out" />
        <span>Loading activity data...</span>
      </div>
    );
  }
  if (!data) {
    return (
      <Empty className="h-full">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon className="lucide--triangle-alert" />
          </EmptyMedia>
          <EmptyTitle>Activity unavailable</EmptyTitle>
          <EmptyDescription>Organisation activity could not be loaded.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const timeSeries = data.timeSeries;
  const series = (value: (point: (typeof timeSeries)[number]) => number): ChartDatum[] =>
    timeSeries.map((point) => ({
      month: point.month,
      label: month.format(new Date(`${point.month.slice(0, 10)}T00:00:00Z`)),
      value: value(point),
    }));

  return (
    <div className="space-y-4">
      <SettingsSectionHeading title="Activity" description="See how your organisation is using Wexler." inset={false} />

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <SummaryMetricCard
          title={<span>Users</span>}
          value={data.totals.users}
          data={series((point) => point.users)}
          tooltip={
            <BreakdownTooltip
              rows={[
                { label: 'Current users', value: data.totals.users },
                { label: 'Blocked users', value: data.totals.blockedUsers },
              ]}
            />
          }
        />
        <SummaryMetricCard
          title="Active users last 30 days"
          value={data.totals.activeUsers}
          data={series((point) => point.activeUsers)}
          tooltip="Current users who created a matter, uploaded documents, created a chronology, or asked Kim in the last 30 days. The preview shows unique active users by month."
        />
        <SummaryMetricCard
          title={<span>Matters</span>}
          value={data.totals.matters}
          data={series((point) => point.matters)}
          tooltip={
            <BreakdownTooltip
              rows={[
                { label: 'Active', value: data.totals.matters },
                { label: 'Drafts', value: data.totals.draftMatters },
                { label: 'Deleted', value: data.totals.deletedMatters },
              ]}
            />
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <MetricChartCard title="Documents" value={data.totals.documents} data={series((point) => point.documents)} />
        <ChronologiesChartCard
          factTotal={data.totals.chronologies}
          documentTotal={data.totals.docChronologies}
          data={timeSeries.map((point) => ({
            month: point.month,
            label: month.format(new Date(`${point.month.slice(0, 10)}T00:00:00Z`)),
            value: point.chronologies + point.docChronologies,
            factChronologies: point.chronologies,
            documentChronologies: point.docChronologies,
          }))}
        />
        <MetricChartCard
          title="Inconsistencies"
          value={data.totals.inconsistencies}
          data={series((point) => point.inconsistencies)}
        />
        <MetricChartCard title="Entities" value={data.totals.entities} data={series((point) => point.entities)} />
      </div>

      <section className="mt-7">
        <div className="mb-4 px-4">
          <h2 className="font-medium text-gray-900">Activity by matter</h2>
          <p className="mt-0.5 text-sm text-gray-500">Restricted matters are shown by ID.</p>
        </div>

        <ServerSideScrollableTable
          table={matterTable}
          searchQuery=""
          onSearchQueryChange={() => undefined}
          plural="matters"
          loading={areMattersLoading}
          totalCount={matterData?.totalCount ?? 0}
          showSearch={false}
          className="h-[44rem] px-2"
        />
      </section>
    </div>
  );
}
